import { ApiError } from './errors';
import type { ApiErrorBody, AuthResponse } from './types';

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  /** JSON-encoded, except a Blob, which is sent as is with its own type (photo uploads). */
  body?: unknown;
  /**
   * Attach bearer token + refresh-on-401 (default true). 'optional' attaches
   * the token when there is one but never refreshes or signs out on a 401
   * (Google sign-in from a guest session, docs/SPEC_TRY.md).
   */
  auth?: boolean | 'optional';
  signal?: AbortSignal;
  /** Return raw Response (for downloads). */
  raw?: boolean;
  /**
   * Share an identical write (same method, path and body) that is already in
   * flight instead of sending it again (default true). A double tap on
   * anything that posts then reaches the server once. Off for telemetry,
   * where two identical events are two events.
   */
  dedupe?: boolean;
}

export interface ClientDeps {
  fetch: typeof fetch;
  baseUrl: string;
  getToken: () => string | null;
  onRefreshed: (res: AuthResponse) => void;
  onAuthLost: () => void;
  onSuspended?: (err: ApiError) => void;
  /** Every API error passes through here before being thrown (cross-cutting reactions). */
  onError?: (err: ApiError) => void;
}

export interface ApiClient {
  request<T>(path: string, opts?: RequestOptions): Promise<T>;
  /** Single-flight refresh: concurrent callers share one in-flight request. */
  refresh(): Promise<AuthResponse>;
}

async function parseError(res: Response): Promise<ApiError> {
  let body: Partial<ApiErrorBody> | null = null;
  try {
    body = (await res.json()) as Partial<ApiErrorBody>;
  } catch {
    body = null;
  }
  const err = body?.error;
  const details = (err?.details ?? {}) as Record<string, unknown>;
  let retryAfter: number | null = null;
  const fromDetails = details.retry_after;
  if (typeof fromDetails === 'number') retryAfter = fromDetails;
  else {
    const h = res.headers.get('Retry-After');
    if (h && !Number.isNaN(Number(h))) retryAfter = Number(h);
  }
  return new ApiError(
    res.status,
    err?.code ?? (res.status === 401 ? 'unauthorized' : res.status >= 500 ? 'internal' : 'error'),
    err?.message ?? res.statusText ?? 'Request failed',
    details,
    retryAfter,
  );
}

export function createApiClient(deps: ClientDeps): ApiClient {
  let inflight: Promise<AuthResponse> | null = null;

  async function doRefresh(): Promise<AuthResponse> {
    let res: Response;
    try {
      res = await deps.fetch(`${deps.baseUrl}/v1/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: { Accept: 'application/json' },
      });
    } catch {
      throw new ApiError(0, 'network', 'Network error');
    }
    if (!res.ok) throw await parseError(res);
    const data = (await res.json()) as AuthResponse;
    deps.onRefreshed(data);
    return data;
  }

  function refresh(): Promise<AuthResponse> {
    if (!inflight) {
      inflight = doRefresh().finally(() => {
        inflight = null;
      });
    }
    return inflight;
  }

  async function send(path: string, opts: RequestOptions): Promise<Response> {
    const headers: Record<string, string> = { Accept: 'application/json' };
    const blob = typeof Blob !== 'undefined' && opts.body instanceof Blob ? opts.body : null;
    if (blob) headers['Content-Type'] = blob.type || 'application/octet-stream';
    else if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
    const token = opts.auth === false ? null : deps.getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
    try {
      return await deps.fetch(`${deps.baseUrl}${path}`, {
        method: opts.method ?? 'GET',
        headers,
        body: blob ?? (opts.body !== undefined ? JSON.stringify(opts.body) : undefined),
        credentials: 'include',
        signal: opts.signal,
      });
    } catch (e) {
      if (e instanceof DOMException && e.name === 'AbortError') throw e;
      throw new ApiError(0, 'network', 'Network error');
    }
  }

  const writes = new Map<string, Promise<unknown>>();

  function writeKey(path: string, opts: RequestOptions): string | null {
    const method = opts.method ?? 'GET';
    if (method === 'GET' || opts.dedupe === false || opts.raw || opts.signal) return null;
    if (typeof Blob !== 'undefined' && opts.body instanceof Blob) return null;
    return `${method} ${path} ${opts.body === undefined ? '' : JSON.stringify(opts.body)}`;
  }

  function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
    const key = writeKey(path, opts);
    if (!key) return send1<T>(path, opts);
    const same = writes.get(key);
    if (same) return same as Promise<T>;
    const p = send1<T>(path, opts).finally(() => writes.delete(key));
    writes.set(key, p);
    return p;
  }

  async function send1<T>(path: string, opts: RequestOptions): Promise<T> {
    let res = await send(path, opts);
    if (res.status === 401 && opts.auth !== false && opts.auth !== 'optional') {
      try {
        await refresh();
      } catch (e) {
        if (e instanceof ApiError && (e.status === 401 || e.status === 403)) deps.onAuthLost();
        throw e instanceof ApiError && e.status !== 0 ? new ApiError(401, 'unauthorized', 'Please log in again.') : e;
      }
      res = await send(path, opts); // retry exactly once
      if (res.status === 401) {
        deps.onAuthLost();
      }
    }
    if (!res.ok) {
      const err = await parseError(res);
      if (err.code === 'account_suspended') deps.onSuspended?.(err);
      deps.onError?.(err);
      throw err;
    }
    if (opts.raw) return res as unknown as T;
    if (res.status === 204) return undefined as T;
    const text = await res.text();
    return (text ? JSON.parse(text) : undefined) as T;
  }

  return { request, refresh };
}
